/**
 * Accesos → Grupos → one grupo (Plan J, J3 — mockup 4, detail half).
 *
 * FIVE TABS NOW, AND THE TWO NEW ONES ARE NOT STUBS
 * -------------------------------------------------
 * **Miembros**, **Ámbito**, **Contenido**, and from J4 **AI** and
 * **Experiencia**, mirrored to `?tab=` the way `UsersAdminPage` already
 * does it. This header used to say "three tabs, and only three", with
 * the reason: two "coming soon" tabs advertise unfinished work on a
 * screen an operator is being taught to trust. That reason survives
 * intact and is what shapes the two additions rather than being spent
 * by them — because the backend is at two different depths and the tabs
 * say which is which instead of averaging it:
 *
 *   * `experience_json` is REAL both ways. `service_groups.parse_experience`
 *     validates it, `group_detail` re-validates it on the way out, and
 *     `home_board_service` provisions and re-syncs a member's board from
 *     it. The Experiencia tab writes it.
 *   * `ai_config_json` is still refused by `assert_not_j4` with a 400
 *     naming the slice, and `GroupOut` does not carry it back. So the AI
 *     tab renders UNKNOWN — every control inert behind one sentence
 *     naming the field and the refusal — rather than a switch whose
 *     "off" nobody wrote. That is the same rule as before, applied to a
 *     field instead of a tab: what must not happen is a control that
 *     looks like it saved.
 *
 * The two writes go through `saveGroup`, which carries the optimistic
 * lock (`expected_updated_at`) exactly like the metadata edit does: two
 * analysts on one group is the ordinary case, and the loser of that race
 * must see a 409 rather than overwrite a scope change with a stale copy.
 *
 * WHAT A MEMBERSHIP CHANGE ACTUALLY DOES
 * --------------------------------------
 * The member endpoints answer **200 with a body**, never 204, and they
 * report BOTH deltas. A caller that submitted a stale list sees the
 * removals it did not intend HERE rather than from the person who lost
 * access. `note` is the backend's own sentence about when the change
 * takes effect, and it is rendered verbatim: the recompute may mint a
 * new effective scope whose FILE does not exist yet, and that is a 503
 * for those people until something builds it. Nothing on this screen
 * paraphrases that.
 *
 * THE ÁMBITO TAB READS `GET /{id}/effective`
 * ------------------------------------------
 * Which costs Postgres rows and one `stat()` per project — no DuckDB
 * reader — and whose per-project half is
 * `service_scopes.describe_scope_projects` verbatim, so this screen, the
 * Ámbitos screen and the runbook's §6 SQL all speak one vocabulary.
 * `warnings` is where the states that COST somebody access are said in
 * words, so they are rendered as callouts and not folded into a chip.
 */

import { Button, Tabs, type TabItem } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  DateCell,
  PageContainer,
  PageHeader,
  Section,
  SectionStack,
  StatusChip,
  type Column,
  type RowAction,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { resolveLocalized, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { ArrowLeft, Trash2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useT } from "@/i18n";
import {
  SCOPE_ALL,
  groupsApi,
  type GroupAiConfig,
  type GroupEffectiveOut,
  type GroupExperience,
  type GroupMemberOut,
  type GroupOut,
  type GroupPermissionsOut,
  type ScopeProjectStatusOut,
} from "@/lib/access-api";
import { draftsApi, projectsApi } from "@/lib/builder-api";
import { parseProjectName } from "@/lib/project-name";
import { DictChip, MemberScopeCell } from "@/pages/admin-access/AccessCells";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import { scopeStateDict } from "@/pages/admin-access/access-status";
import { AddMembersModal } from "@/pages/admin-access/AddMembersModal";
import { GroupAiTab } from "@/pages/admin-access/GroupAiTab";
import { GroupExperienceTab } from "@/pages/admin-access/GroupExperienceTab";
import {
  PermissionMatrix,
  type CellState,
  type ProjectRef,
  type ReportPill,
  splitPending,
} from "@/pages/admin-access/PermissionMatrix";
import { reloadThenReport } from "@/pages/admin-access/reload-after-failure";

type GroupTab = "members" | "scope" | "content" | "ai" | "experience";

export function readTab(raw: string | null): GroupTab {
  if (
    raw === "scope" ||
    raw === "content" ||
    raw === "ai" ||
    raw === "experience"
  ) {
    return raw;
  }
  return "members";
}

export function GroupDetailPage(): JSX.Element {
  const { groupId = "" } = useParams<{ groupId: string }>();
  const { t, locale } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTab(searchParams.get("tab"));

  const [group, setGroup] = useState<GroupOut | null>(null);
  const [permissions, setPermissions] = useState<GroupPermissionsOut | null>(
    null,
  );
  const [effective, setEffective] = useState<GroupEffectiveOut | null>(null);
  const [reports, setReports] = useState<ReportPill[]>([]);
  const [projects, setProjects] = useState<ProjectRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const states = useMemo(() => scopeStateDict(t), [t]);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [detail, eff, drafts, projectList] = await Promise.all([
        groupsApi.get(groupId),
        groupsApi.effective(groupId),
        draftsApi.list(),
        projectsApi.list(),
      ]);
      setGroup(detail);
      setPermissions(detail.permissions);
      setEffective(eff);
      setReports(
        drafts.drafts
          .map((d) => ({
            key: d.key,
            label:
              resolveLocalized(
                d.report_config.display_name as DisplayLabel | undefined,
                locale,
              ) || d.key,
            projectSlug: d.project_slug ?? null,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
      setProjects(
        projectList.projects.map((p) => ({
          slug: p.slug,
          label:
            resolveLocalized(parseProjectName(p.name) as DisplayLabel, locale) ||
            p.slug,
        })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [groupId, locale]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const savePermissions = async (
    pending: Map<string, CellState>,
  ): Promise<void> => {
    setSaving(true);
    setError(null);
    const { reportUpserts, reportDeletes, projectUpserts, projectDeletes } =
      splitPending(pending);
    try {
      let latest: GroupPermissionsOut | null = null;
      if (reportUpserts.length > 0) {
        latest = await groupsApi.setReportPermissions(groupId, reportUpserts);
      }
      if (reportDeletes.length > 0) {
        latest = await groupsApi.deleteReportPermissions(groupId, reportDeletes);
      }
      if (projectUpserts.length > 0) {
        latest = await groupsApi.setProjectPermissions(groupId, projectUpserts);
      }
      if (projectDeletes.length > 0) {
        latest = await groupsApi.deleteProjectPermissions(
          groupId,
          projectDeletes,
        );
      }
      // Re-render from the matrix the SERVER returned, never from what
      // was sent: a delete is not a `false`, and the response is what
      // tells them apart.
      if (latest !== null) setPermissions(latest);
    } catch (err) {
      // refresh() starts with setError(null): the save's own message is
      // published AFTER the reload, or it never renders.
      const message = err instanceof Error ? err.message : String(err);
      await reloadThenReport(message, refresh, setError);
    } finally {
      setSaving(false);
    }
  };

  /**
   * The one writer for both J4 documents.
   *
   * `expected_updated_at` comes from the group as LOADED, so a tab left
   * open while somebody else edited the group earns a 409 naming both
   * timestamps instead of overwriting them. `note` is the backend's own
   * sentence about the consequence and is rendered verbatim — a scope
   * change reported through this path says so, and nothing here
   * paraphrases it.
   */
  const saveGroup = async (payload: {
    ai_config_json?: GroupAiConfig;
    experience_json?: GroupExperience;
  }): Promise<void> => {
    if (group === null) return;
    setSaving(true);
    setError(null);
    try {
      const out = await groupsApi.update(groupId, {
        ...payload,
        expected_updated_at: group.updated_at,
      });
      setNotice(out.note);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (member: GroupMemberOut): Promise<void> => {
    setError(null);
    try {
      const out = await groupsApi.removeMember(groupId, member.user_id);
      setNotice(out.note);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const memberColumns: Column<GroupMemberOut>[] = [
    {
      id: "person",
      header: t("access.groups.members.col.person"),
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">{row.name}</div>
          <div className="truncate font-mono text-[13px] text-neutral-500">
            {row.email}
          </div>
        </div>
      ),
    },
    {
      id: "role",
      header: t("access.groups.members.col.role"),
      width: "w-28",
      cell: (row) => <span className="text-[13px] text-neutral-700">{row.role}</span>,
    },
    {
      id: "status",
      header: t("access.groups.members.col.status"),
      width: "w-36",
      cell: (row) => (
        <StatusChip
          label={
            row.is_active
              ? t("access.people.active")
              : t("access.people.inactive")
          }
          tone={row.is_active ? "success" : "neutral"}
        />
      ),
    },
    {
      id: "effective",
      header: t("access.groups.members.col.effective"),
      // Wider than the `w-52` it carried while this cell was an id and a
      // grey line: the verdict chip is a phrase in both languages («el
      // recorte de este grupo»), and a chip that wraps mid-word is the
      // shape of a label an operator skims past.
      width: "w-64",
      // Judged against THIS group's scope, not rendered as a bare fact.
      // A member whose union resolves to `all` reads every row, so the
      // cut on this page decides nothing for them — and until this cell
      // carried a verdict they looked exactly like a member the cut does
      // govern.
      //
      // `group` is non-null wherever these rows render (the table is
      // behind `loading || group === null`), but the columns are built
      // outside that guard, so the type is honest here: `null` is passed
      // through as "no claim" rather than swapped for `SCOPE_ALL`, which
      // would read as "this group restricts nobody".
      cell: (row) => (
        <MemberScopeCell
          groupScopeId={group === null ? null : group.scope_id}
          effectiveScopeId={row.effective_scope_id}
          groupScopeCount={row.group_scope_ids.length}
          t={t}
        />
      ),
    },
    {
      id: "added",
      header: t("access.groups.members.col.added"),
      width: "w-48",
      cell: (row) => (
        <div className="min-w-0">
          <DateCell
            value={row.added_at}
            locale={locale}
            mode="absolute"
            invalidHint={t("access.common.badDate")}
          />
          {row.added_by_email !== null && (
            <span className="block truncate font-mono text-[13px] text-neutral-500">
              {row.added_by_email}
            </span>
          )}
        </div>
      ),
    },
  ];

  const memberActions: RowAction<GroupMemberOut>[] = [
    {
      id: "remove",
      label: t("access.groups.members.remove"),
      icon: <Trash2 size={14} />,
      danger: true,
      onSelect: (row) => void removeMember(row),
    },
  ];

  const projectColumns: Column<ScopeProjectStatusOut>[] = [
    {
      id: "project",
      header: t("access.scopes.detail.col.project"),
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">
            {row.project_name}
          </div>
          <div className="truncate font-mono text-[13px] text-neutral-500">
            {row.project_slug}
          </div>
        </div>
      ),
    },
    {
      id: "state",
      header: t("access.scopes.detail.col.state"),
      width: "w-44",
      cell: (row) => (
        <DictChip
          value={row.state}
          dictionary={states}
          t={t}
          title={row.not_applicable_detail ?? row.error_detail}
        />
      ),
    },
    {
      id: "rows",
      header: t("access.scopes.detail.col.rows"),
      align: "right",
      width: "w-32",
      cell: (row) =>
        row.row_count === null ? (
          <span className="text-[13px] text-neutral-500">
            {t("access.common.notComputed")}
          </span>
        ) : (
          <span className="text-neutral-900">{row.row_count}</span>
        ),
    },
    {
      id: "built",
      header: t("access.scopes.detail.col.built"),
      width: "w-44",
      cell: (row) => (
        <DateCell
          value={row.built_at ?? row.started_at}
          locale={locale}
          mode="absoluteWithTime"
          invalidHint={t("access.common.badDate")}
        />
      ),
    },
  ];

  const tabs: TabItem[] = [
    { id: "members", label: t("access.groups.tab.members") },
    { id: "scope", label: t("access.groups.tab.scope") },
    { id: "content", label: t("access.groups.tab.content") },
    { id: "ai", label: t("access.groups.tab.ai") },
    { id: "experience", label: t("access.groups.tab.experience") },
  ];

  return (
    <PageContainer wide>
      <PageHeader
        eyebrow={
          <Link
            to="/admin/access/groups"
            className="inline-flex items-center gap-1 text-[13px] font-medium text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft size={13} />
            {t("access.groups.title")}
          </Link>
        }
        title={group?.name ?? groupId}
        subtitle={group?.description === "" ? undefined : group?.description}
        actions={
          tab === "members" ? (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<UserPlus size={14} />}
              onClick={() => setAdding(true)}
            >
              {t("access.groups.members.add")}
            </Button>
          ) : undefined
        }
      />
      <AccessTabs active="groups" />

      <div className="mb-4 border-b border-neutral-200">
        <Tabs
          items={tabs}
          activeId={tab}
          onChange={(id: string) =>
            setSearchParams(id === "members" ? {} : { tab: id }, {
              replace: true,
            })
          }
          variant="underline"
          size="sm"
        />
      </div>

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}
      {notice !== null && (
        <Callout tone="info" title={t("access.groups.members.noticeTitle")}>
          <span className="whitespace-pre-wrap">{notice}</span>
        </Callout>
      )}

      {loading || group === null ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
          <Spinner size={16} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </div>
      ) : tab === "members" ? (
        <Section flush>
          <DataTable
            columns={memberColumns}
            rows={group.members}
            rowKey={(row) => row.user_id}
            actions={memberActions}
            caption={t("access.groups.tab.members")}
            footnote={t("access.groups.members.footnote")}
          />
        </Section>
      ) : tab === "scope" ? (
        <SectionStack>
          {(effective?.warnings ?? []).map((warning) => (
            <Callout
              key={warning}
              tone="warning"
              title={t("access.groups.scope.warningTitle")}
            >
              <span className="whitespace-pre-wrap">{warning}</span>
            </Callout>
          ))}

          <Section
            title={t("access.groups.scope.title")}
            description={t("access.groups.scope.desc")}
          >
            <dl className="grid gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-[13px] font-semibold text-neutral-600">
                  {t("access.groups.form.scope")}
                </dt>
                <dd className="text-sm text-neutral-900">
                  {group.scope_id === SCOPE_ALL
                    ? t("access.scopes.all")
                    : group.scope_name}
                </dd>
              </div>
              <div>
                <dt className="text-[13px] font-semibold text-neutral-600">
                  {t("access.scopes.form.id")}
                </dt>
                <dd className="font-mono text-sm text-neutral-900">
                  {group.scope_id}
                </dd>
              </div>
              <div>
                <dt className="text-[13px] font-semibold text-neutral-600">
                  {t("access.groups.col.members")}
                </dt>
                <dd className="text-sm text-neutral-900">
                  {group.member_count}
                </dd>
              </div>
            </dl>
            <p className="mt-3">
              <Link
                to={`/admin/access/scopes/${encodeURIComponent(group.scope_id)}`}
                className="text-sm font-medium text-primary-ink underline underline-offset-2"
              >
                {t("access.groups.scope.openScope")}
              </Link>
            </p>
          </Section>

          <Section
            title={t("access.scopes.detail.projects")}
            description={t("access.groups.scope.projectsDesc")}
            flush
          >
            <DataTable
              columns={projectColumns}
              rows={effective?.projects ?? []}
              rowKey={(row) => row.project_slug}
              caption={t("access.scopes.detail.projects")}
            />
          </Section>
        </SectionStack>
      ) : tab === "ai" ? (
        <GroupAiTab
          group={group}
          effective={effective}
          saving={saving}
          onSave={(ai) => saveGroup({ ai_config_json: ai })}
        />
      ) : tab === "experience" ? (
        <GroupExperienceTab
          group={group}
          saving={saving}
          onSave={(experience) => saveGroup({ experience_json: experience })}
        />
      ) : group.covers_all_reports ? (
        // Portal migration 077: the base group's actions are fixed
        // (D-G2) and the server refuses a matrix edit with a 409.
        <Callout tone="info" title={t("access.groups.base.contentTitle")}>
          {t("access.groups.base.contentBody")}
        </Callout>
      ) : permissions === null ? (
        <p className="text-sm text-neutral-500">{t("common.loading")}</p>
      ) : (
        <Section
          title={t("access.groups.tab.content")}
          description={t("access.groups.content.desc")}
        >
          <PermissionMatrix
            permissions={permissions}
            reports={reports}
            projects={projects}
            saving={saving}
            onSave={(pending) => void savePermissions(pending)}
          />
        </Section>
      )}

      <AddMembersModal
        open={adding}
        groupId={groupId}
        current={group?.members ?? []}
        onClose={() => setAdding(false)}
        onAdded={(note) => {
          setAdding(false);
          setNotice(note);
          void refresh();
        }}
        onChanged={(note) => {
          setNotice(note);
          void refresh();
        }}
      />
    </PageContainer>
  );
}
